import { z } from "zod";
import { ExternalIdEntrySchema } from "./external-id.schema";

// ── Promotion (definition) ──────────────────────────────────

export const PromotionSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().nullable().describe("Account"),
  brand_id: z.string().uuid().nullable().describe("Brand"),
  name: z.string().describe("Name"),
  code: z.string().nullable().describe("Code"),
  description: z.string().nullable().describe("Description"),
  discount_type: z.enum(["percentage", "fixed"]).describe("Discount Type"),
  discount_value: z.number().describe("Discount Value"),
  scope: z.enum(["proposal", "line_item"]).describe("Scope"),
  target_rule: z.record(z.unknown()).nullable().describe("Target Rule"),
  valid_from: z.string().nullable().describe("Valid From"),
  valid_to: z.string().nullable().describe("Valid To"),
  usage_limit: z.number().nullable().describe("Usage Limit"),
  usage_count: z.number().describe("Usage Count"),
  stackable: z.boolean().describe("Stackable"),
  priority: z.number().describe("Priority"),
  is_active: z.boolean().describe("Active"),
  custom_fields: z.record(z.unknown()).default({}).describe("Custom Fields"),
  created_at: z.string().nullable().describe("Created At"),
  updated_at: z.string().nullable().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});

const PromotionInsertBase = z.object({
  name: z.string().describe("Name"),
  code: z.string().nullable().optional().describe("Code"),
  description: z.string().nullable().optional().describe("Description"),
  discount_type: z.enum(["percentage", "fixed"]).describe("Discount Type"),
  discount_value: z.number().positive().describe("Discount Value"),
  scope: z.enum(["proposal", "line_item"]).describe("Scope"),
  brand_id: z.string().uuid().nullable().optional().describe("Brand"),
  target_rule: z
    .record(z.unknown())
    .nullable()
    .optional()
    .describe("Target Rule"),
  valid_from: z.string().nullable().optional().describe("Valid From"),
  valid_to: z.string().nullable().optional().describe("Valid To"),
  usage_limit: z.number().nullable().optional().describe("Usage Limit"),
  stackable: z.boolean().optional().describe("Stackable"),
  priority: z.number().optional().describe("Priority"),
  is_active: z.boolean().optional().describe("Active"),
});

export const PromotionInsertSchema = PromotionInsertBase.refine(
  (d) => d.discount_type !== "percentage" || d.discount_value <= 100,
  {
    message: "Percentage discount cannot exceed 100",
    path: ["discount_value"],
  },
);

export const PromotionUpdateSchema = PromotionInsertBase.partial().refine(
  (d) =>
    d.discount_type !== "percentage" ||
    !d.discount_value ||
    d.discount_value <= 100,
  {
    message: "Percentage discount cannot exceed 100",
    path: ["discount_value"],
  },
);

export const PromotionQuerySchema = z.object({
  is_active: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  scope: z.enum(["proposal", "line_item"]).optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  discount_type: z.enum(["percentage", "fixed"]).optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  brand_id: z.string().uuid().optional(),
});

export type Promotion = z.infer<typeof PromotionSchema>;
